import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { WorkOrderDocumentService } from './work-order-document.service';
import { DocumentDto, UserDto } from '@app/generated/model';
import { ActivatedRoute } from '@angular/router';
import { WebSocketService } from '@app/core/services/websocket.service';
import { WorkOrderService } from '@app/work-order/work-order.service';
import { WorkOrderDetailsService } from '@app/work-order/work-order-details.service';
import { BadgeNotification } from '@app/core/model/badge-notification.model';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { LoadDocumentPage, LoadWorkOrder, LoadOpenedTabs, LoadActiveTab } from '@dwo/actions';
import { getCurrentUser, getPageForDocument, getOpenedTabIds, getActiveTabId } from '@dwo/selectors';
import { OfflineDataDb } from '@app/core/db/offline-data';
import { FeatureFlagService } from '@app/core/services/feature-flag.service';
import { FeatureFlagKey } from '@dwo/enums';
import { blobToDataUrl } from '@app/utils';
import { LoggingService } from '@app/shared/services/logging.service';
import { PDF_ZOOM_LEVELS } from '@app/shared/constants';

const NOT_AVAILABLE_OFFLINE = 'workOrderDocuments.notAvailableOffline';

interface WebkitFullscreenElement extends HTMLElement {
  webkitRequestFullscreen?(): Promise<void> | void;
}
interface WebkitFullscreenDocument extends Document {
  webkitExitFullscreen?(): Promise<void> | void;
  webkitFullscreenElement?: Element | null;
}

@Component({
  selector: 'app-work-order-documents',
  templateUrl: './work-order-documents.component.html',
  styleUrls: ['./work-order-documents.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WorkOrderDocumentsComponent implements OnInit, AfterViewInit, OnDestroy {
  workOrderId: string;
  projectId: string;
  documentIds: {};
  documentNotifications: BadgeNotification[];
  documents: DocumentDto[];
  timeDocuments: DocumentDto[] = [];
  private notificationSubscription$: Subscription;
  private destroy$ = new Subject<void>();
  currentUser: string;
  displayedDocuments: DocumentDto[] = [];
  selectedDocument: DocumentDto | null = null;
  docSrc: string | null = null;
  documentError: string | null = null;
  private currentLoadToken = 0;
  activeTabIndex = 0;
  pdfViewerHeight = '80vh';
  readonly pdfZoomLevels = PDF_ZOOM_LEVELS;
  readonly isTouchDevice: boolean = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
  private resizeObserver: ResizeObserver;
  @ViewChild('tabGroupWrapper') tabGroupWrapperRef: ElementRef<HTMLElement>;
  @ViewChild('pdfViewerWrapper') pdfViewerWrapperRef: ElementRef<HTMLElement>;
  isFullscreen = false;
  constructor(
    private route: ActivatedRoute,
    private store: Store,
    private documentService: WorkOrderDocumentService,
    private webSocketService: WebSocketService,
    private workOrderDetailsService: WorkOrderDetailsService,
    private workOrderService: WorkOrderService,
    private httpClient: HttpClient,
    private offlineData: OfflineDataDb,
    private ngZone: NgZone,
    private featureFlagService: FeatureFlagService,
    private log: LoggingService,
  ) {
    this.workOrderDetailsService
      .getData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((workOrder) => {
        if (workOrder) {
          this.workOrderId = workOrder.id;
          this.projectId = workOrder.projectId;
        }
      });

    this.documentService.getData().subscribe((documents) => {
      if (documents) {
        this.documents = documents.filter((doc) => doc.documentType !== 'LINK');
        this.timeDocuments = documents.filter((doc) => doc.documentType === 'LINK');
        this.sortDocuments();
      }
    });
  }

  get showTimeDocuments(): boolean {
    return (
      this.timeDocuments?.length > 0 &&
      this.featureFlagService.isEnabled(FeatureFlagKey.ShowTimeDocuments, this.projectId)
    );
  }

  hasListTab(): boolean {
    return !!(this.documents?.length || this.showTimeDocuments);
  }

  private get openableDocuments(): DocumentDto[] {
    const documents = this.documents ?? [];
    return this.showTimeDocuments ? [...documents, ...this.timeDocuments] : documents;
  }

  ngAfterViewInit(): void {
    if (!this.tabGroupWrapperRef) return;
    this.resizeObserver = new ResizeObserver(() => this.updatePdfHeight());
    this.resizeObserver.observe(this.tabGroupWrapperRef.nativeElement);
    const splitPanelEl = this.tabGroupWrapperRef.nativeElement.closest('.split-panel');
    if (splitPanelEl) {
      this.resizeObserver.observe(splitPanelEl);
    }
    this.updatePdfHeight();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updatePdfHeight();
  }

  private updatePdfHeight(): void {
    const wrapperEl = this.tabGroupWrapperRef?.nativeElement;
    if (!wrapperEl) {
      return;
    }
    const wrapperBottom = wrapperEl.getBoundingClientRect().bottom;
    const splitPanelEl = wrapperEl.closest('.split-panel') as HTMLElement | null;
    const containerBottom = splitPanelEl?.getBoundingClientRect().bottom ?? window.innerHeight;
    const available = containerBottom - wrapperBottom - 1;
    if (available > 50) {
      this.ngZone.run(() => {
        this.pdfViewerHeight = `${available}px`;
      });
    }
  }

  ngOnInit() {
    this.workOrderDetailsService.workOrder$.pipe(takeUntil(this.destroy$)).subscribe((workOrder) => {
      if (!workOrder) {
        const workOrderId = this.route.parent?.parent?.snapshot.paramMap.get('id');
        if (workOrderId) {
          this.store.dispatch(new LoadWorkOrder(workOrderId));
        }
      }
    });

    this.store.pipe(select(getCurrentUser)).subscribe((user: UserDto) => {
      this.currentUser = user?.username;
      if (!this.currentUser) return;
      this.store
        .pipe(select(getOpenedTabIds(this.currentUser)), takeUntil(this.destroy$))
        .subscribe((openedTabIds: string[]) => {
          if (openedTabIds && this.documents) {
            this.displayedDocuments = this.openableDocuments.filter((doc) => openedTabIds.includes(doc.id));
          }
        });
      this.store
        .pipe(select(getActiveTabId(this.currentUser)), takeUntil(this.destroy$))
        .subscribe((activeTabId: string | null) => {
          if (activeTabId && this.displayedDocuments) {
            this.selectedDocument = this.displayedDocuments.find((doc) => doc.id === activeTabId) || null;
            this.activeTabIndex = this.computeSelectedIndex();
            if (this.selectedDocument) {
              if (!this.is3dDocument(this.selectedDocument)) {
                this.loadPdf(this.selectedDocument);
              }
            }
            // Recalculate height after the viewer is rendered into the DOM
            setTimeout(() => this.updatePdfHeight(), 0);
          } else if (!activeTabId) {
            this.selectedDocument = null;
          }
        });
    });
    this.initWebSocket();
  }

  initWebSocket(): void {
    this.notificationSubscription$ = this.webSocketService.badgeNotifications$.subscribe(
      (notifications: BadgeNotification[]) => {
        if (notifications == null) {
          return;
        }

        const workOrderNotification = notifications.filter(
          (notification: BadgeNotification) =>
            notification.name.endsWith('WorkOrderEntity') && notification.id === this.workOrderId,
        )[0];

        if (workOrderNotification) {
          this.documentNotifications = workOrderNotification.children;

          // create a map to avoid doing filter
          this.documentIds = this.documentNotifications.reduce((map, notification: BadgeNotification) => {
            map[notification.id] = notification;
            return map;
          }, {});

          if (this.documentNotifications.length > 0) {
            // wait 5 seconds before we send notification back
            setTimeout(() => this.webSocketService.sendNotificationsViewed(this.documentNotifications), 5000);
          }
        } else {
          this.documentNotifications = [];
          this.documentIds = {};
        }
      },
    );
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
    this.notificationSubscription$.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  hasNotification(documentId): boolean {
    return this.documentIds && this.documentIds[documentId];
  }

  preparePreviewDocument() {
    this.workOrderService.navigationShowDocumentMenu = true;
    this.route.url.subscribe((url) => {
      this.workOrderService.navigationBackRoute = url.join();
    });
  }

  sortDocuments() {
    this.documents.sort((a, b) => {
      if (a.documentType === 'JOB_PACKAGE') {
        return -1;
      }
      if (b.documentType === 'JOB_PACKAGE') {
        return 1;
      }
      return 0;
    });
  }

  onPdfPageChange(docId: string, page: number) {
    this.store.dispatch(new LoadDocumentPage(docId, page, this.currentUser));
  }

  getPageForDocument(docId: string): Observable<number> {
    return this.store.pipe(select(getPageForDocument(this.currentUser, docId)));
  }

  addToTabs(doc: DocumentDto) {
    if (!doc) {
      return;
    }
    const openedTabIds = this.displayedDocuments.map((d) => d.id);
    if (openedTabIds.includes(doc.id)) {
      this.store.dispatch(new LoadActiveTab(doc.id, this.currentUser));
    } else {
      openedTabIds.push(doc.id);
      this.store.dispatch(new LoadOpenedTabs(openedTabIds, this.currentUser));
      this.store.dispatch(new LoadActiveTab(doc.id, this.currentUser));
    }
  }

  removeFromTabs(doc: DocumentDto) {
    const openedTabIds = this.displayedDocuments.map((d) => d.id).filter((id) => id !== doc.id);
    this.store.dispatch(new LoadOpenedTabs(openedTabIds, this.currentUser));
    if (this.selectedDocument?.id === doc.id) {
      const newActiveId = openedTabIds.length ? openedTabIds[openedTabIds.length - 1] : null;
      this.store.dispatch(new LoadActiveTab(newActiveId, this.currentUser));
    }
  }

  onTabChange(event: MatTabChangeEvent) {
    const offset = this.hasListTab() ? 1 : 0;
    const relativeIdx = event.index - offset;
    this.activeTabIndex = event.index;
    if (relativeIdx >= 0 && relativeIdx < this.displayedDocuments.length) {
      const doc = this.displayedDocuments[relativeIdx];
      this.store.dispatch(new LoadActiveTab(doc.id, this.currentUser));
    } else {
      this.store.dispatch(new LoadActiveTab(null, this.currentUser));
    }
  }

  computeSelectedIndex(): number {
    const isDocsList = this.hasListTab();
    if (!this.selectedDocument) {
      return 0;
    }
    const docIndex = this.displayedDocuments.findIndex((d) => d.id === this.selectedDocument!.id);
    if (docIndex === -1) {
      return 0;
    }
    return (isDocsList ? 1 : 0) + docIndex;
  }

  isListTabActive(): boolean {
    return this.hasListTab() && this.activeTabIndex === 0;
  }

  is3dDocument(doc: DocumentDto): boolean {
    return (
      doc?.documentType === DocumentDto.DocumentTypeEnum.Model3D ||
      ['FBX', 'GLB', 'GLTF'].includes(doc?.fileType?.toUpperCase())
    );
  }
  isVideoDocument(doc: DocumentDto): boolean {
    return (
      doc?.documentType === DocumentDto.DocumentTypeEnum.Video ||
      ['MP4', 'MOV', 'WEBM', 'AVI'].includes(doc?.fileType?.toUpperCase())
    );
  }

  async loadPdf(document: DocumentDto) {
    this.documentError = null;
    if (!document?.link) {
      this.docSrc = '';
      return;
    }

    const url = document.link.toString();
    // Guards against a slower previous load overwriting a faster, more recent tab selection.
    const loadToken = ++this.currentLoadToken;
    const documentProperties = { DocumentId: document.id, FileType: document.fileType };

    // Try offline storage first
    const offlineFile = await this.offlineData.workOrderFiles
      .where('url')
      .equals(url)
      .first()
      .catch(() => null);

    if (offlineFile) {
      // data: URLs render reliably in the PDF viewer's iframe; blob: URLs can fail silently on iOS/Safari under a service worker
      const offlineBlob = new Blob([offlineFile.data], { type: offlineFile.type || 'application/pdf' });
      const dataUrl = await blobToDataUrl(offlineBlob);
      if (loadToken === this.currentLoadToken) {
        this.docSrc = dataUrl;
        this.log.event('DocumentOpened', { ...documentProperties, Source: 'offline' });
      }
      return;
    }

    this.httpClient.get(url, { responseType: 'blob' }).subscribe({
      next: async (response: Blob | HttpErrorResponse) => {
        // The global HTTP error interceptor re-emits failures as values instead of errors.
        if (!(response instanceof Blob)) {
          this.setDocumentError(response);
          this.log.event('DocumentLoadFailed', { ...documentProperties, Status: response?.status });
          return;
        }
        const dataUrl = await blobToDataUrl(response);
        if (loadToken === this.currentLoadToken) {
          this.docSrc = dataUrl;
          this.log.event('DocumentOpened', { ...documentProperties, Source: 'network' });
        }
      },
      error: (err) => {
        console.warn('Failed to load PDF from network and no offline copy found', err);
        this.setDocumentError(err);
        this.log.event('DocumentLoadFailed', { ...documentProperties, Status: err?.status });
      },
    });
  }

  onPdfLoadingFailed(error: unknown): void {
    console.warn('[Documents] PDF viewer failed to load document:', error);
    this.docSrc = '';
    this.documentError = 'workOrderDocuments.loadFailed';
  }

  get isOfflineError(): boolean {
    return this.documentError === NOT_AVAILABLE_OFFLINE;
  }

  private setDocumentError(err: { status?: number }): void {
    this.docSrc = '';
    if (!navigator.onLine) {
      this.documentError = NOT_AVAILABLE_OFFLINE;
      return;
    }
    const status = err?.status;
    // A cross-origin 401/403 is often reported as status 0 (CORS hides the real status),
    // so treat those as an authorization problem rather than a generic load failure.
    this.documentError =
      status === 401 || status === 403 || status === 0
        ? 'workOrderDocuments.unauthorized'
        : 'workOrderDocuments.loadFailed';
  }

  private get fullscreenElement(): Element | null {
    const doc = document as WebkitFullscreenDocument;
    return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
  }

  async toggleFullscreen(): Promise<void> {
    const el = this.pdfViewerWrapperRef?.nativeElement as WebkitFullscreenElement | undefined;
    if (!el) return;

    try {
      if (!this.fullscreenElement) {
        await (el.requestFullscreen?.() ?? el.webkitRequestFullscreen?.());
      } else {
        const doc = document as WebkitFullscreenDocument;
        await (doc.exitFullscreen?.() ?? doc.webkitExitFullscreen?.());
      }
    } catch (err) {
      console.warn('Fullscreen toggle failed', err);
    }
  }

  @HostListener('document:fullscreenchange')
  @HostListener('document:webkitfullscreenchange')
  onFullscreenChange(): void {
    this.isFullscreen = !!this.fullscreenElement;
    setTimeout(() => this.updatePdfHeight(), 0);
  }
}
